import { useCallback, useRef, useEffect } from 'react';

import useRect from './useRect';

const noop = () => {};

// If the event target is not a node, assume it is the window
const resolveScrollInfo = (target, viewportNodeRect) => {
  if (!target.getBoundingClientRect) {
    return {
      height: window.innerHeight,
      scrollTop: document.documentElement.scrollTop,
      scrollHeight: document.documentElement.scrollHeight
    };
  } else {
    return {
      height: viewportNodeRect.height,
      scrollTop: target.scrollTop,
      scrollHeight: target.scrollHeight
    };
  }
};

const resolveViewportNode = (node) => node === null ? window : node;

const useScrollProgress = (onProgress = noop) => {
  const viewportNode = useRef(null);
  const [ viewportNodeRect, viewportNodeRectBind ] = useRect();

  const onScroll = useCallback(
    e => {
      const {
        height,
        scrollTop,
        scrollHeight
      } = resolveScrollInfo(e.target, viewportNodeRect);

      const progress = scrollTop / (scrollHeight - height);

      onProgress(Math.max(Math.min(progress, 1), 0));
    },
    [ onProgress, viewportNodeRect ]
  );

  const attachEventsCleanup = useRef(null);

  const attachEvents = useCallback((node) => {
    const resolvedNode = resolveViewportNode(node);

    attachEventsCleanup.current && attachEventsCleanup.current();

    resolvedNode.addEventListener('scroll', onScroll, {
      capture: false,
      passive: true
    });

    attachEventsCleanup.current = () => {
      resolvedNode.removeEventListener('scroll', onScroll, {
        capture: false,
        passive: true
      });
    }
  }, [ onScroll ]);

  const bindRect = useCallback(viewportNodeRectBind, []);

  const onViewportNode = useCallback(
    node => {
      viewportNode.current = node;
      viewportNodeRectBind(viewportNode.current);
      attachEvents(viewportNode.current);
    },
    []
  );

  useEffect(() => {
    attachEvents(viewportNode.current);
  }, [ onScroll ]);

  useEffect(() => () => {
    attachEventsCleanup.current && attachEventsCleanup.current();
  }, []);

  return { onViewportNode };
};

export default useScrollProgress;
